<i18n>
  {
    "en": {
      "unit": {
        "businessHours": "businessHours",
        "day": "day",
        "week": "week",
        "month": "month",
        "year": "year"
      }
    },
    "zh-tw": {
      "unit": {
        "businessHours": "營業時段",
        "day": "日",
        "week": "週",
        "month": "月",
        "year": "年"
      }
    }
  }
</i18n>

<template>
  <f7-page>
    <f7-navbar title="報表查詢結果" back-link="back" />
    <div style="display: flex;">
      <f7-button
        @click="outputExcel"
        raised
        fill
        style="display: flex; justify-content: center; margin: .5rem .5rem .5rem auto; ">
        下載報表
      </f7-button>
    </div>

    <div v-if="customStatistics && customForHereStatistics && tableNumbers" style="display: flex; flex-direction: row;">
      <div style="flex: 0 0 auto;" class="data-table">
        <table>
          <thead>
            <tr>
              <th id="time-label" class="label-cell">時間({{ $t(`unit.${searchUnit}`) }})</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(data, index) in sortedCustomStatistics"
              :key="`statistics-search-results-title-${index}`">
              <td id="time-interval" class="label-cell">{{ format(data.interval) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style="flex: 1 1 auto;" class="data-table">
        <table>
          <thead>
            <tr>
              <th class="numeric-cell">營業額</th>
              <th class="numeric-cell">來客數</th>
              <th class="numeric-cell">訂單數</th>
              <th class="numeric-cell">平均客單價</th>
              <th class="numeric-cell">悠遊卡</th>
              <th class="numeric-cell">悠遊卡 (扣除手續費 {{ store.easyCardProcessingFee || 0 }}%)</th>
              <th class="numeric-cell">信用卡</th>
              <th class="numeric-cell">信用卡 (扣除手續費 {{ store.creditCardProcessingFee || 0 }}%)</th>
              <th class="numeric-cell">線上 LINE Pay</th>
              <th class="numeric-cell">線上 LINE Pay (扣除手續費 {{ store.linePayProcessingFee || 0 }}%)</th>
              <th class="numeric-cell">現金</th>
              <th class="numeric-cell">內用訂單數</th>
              <th class="numeric-cell">翻桌率</th>
              <th class="numeric-cell">退款總金額</th>
              <th class="numeric-cell">悠遊卡 (退款)</th>
              <th class="numeric-cell">信用卡 (退款)</th>
              <th class="numeric-cell">線上 LNE Pay (退款)</th>
              <th class="numeric-cell">現金 (退款)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(data, index) in sortedCustomStatistics"
              :key="`statistics-search-results-${index}`">
              <td class="numeric-cell">{{ data.total.paid.total || 0 }}</td>
              <td class="numeric-cell">{{ data.product.paid.total || 0 }}</td>
              <td class="numeric-cell">{{ data.order.paid.total || 0 }}</td>
              <td class="numeric-cell">{{ parseFloat((data.total.paid.total / data.product.paid.total || 0).toFixed(0)) }}</td>
              <td class="numeric-cell">{{ data.total.paid.easyCard || 0 }}</td>
              <td class="numeric-cell">{{ parseFloat((data.total.paid.easyCard * (100 - (store.easyCardProcessingFee || 0)) / 100 || 0).toFixed(0)) }}</td>
              <td class="numeric-cell">{{ (data.total.paid.creditCard || 0) + (data.total.paid.chinaUnionPay || 0) }}</td>
              <td class="numeric-cell">{{ parseFloat((((data.total.paid.creditCard || 0) + (data.total.paid.chinaUnionPay || 0)) * (100 - (store.creditCardProcessingFee || 0)) / 100 || 0).toFixed(0)) }}</td>
              <td class="numeric-cell">{{ data.total.paid.linePayOnline || 0 }}</td>
              <td class="numeric-cell">{{ linePayAmountWithoutProcessingFee(data.total.paid.linePayOnline) }}</td>
              <td class="numeric-cell">{{ data.total.paid.cash || 0 }}</td>
              <td class="numeric-cell">{{ customForHereStatistics[data.key] ? customForHereStatistics[data.key].order.paid.total || 0 : 0 }}</td>
              <td class="numeric-cell">{{ parseFloat(((customForHereStatistics[data.key] ? customForHereStatistics[data.key].order.paid.total || 0 : 0) / tableNumbers.length).toFixed(1)) }}</td>
              <td class="numeric-cell">{{ data.total.refunded.total || 0 }}</td>
              <td class="numeric-cell">{{ data.total.refunded.easyCard || 0 }}</td>
              <td class="numeric-cell">{{ (data.total.refunded.creditCard || 0) + (data.total.refunded.chinaUnionPay || 0) }}</td>
              <td class="numeric-cell">{{ data.total.refunded.linePayOnline || 0 }}</td>
              <td class="numeric-cell">{{ data.total.refunded.cash || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </f7-page>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import * as XLSX from 'xlsx'
import FileSaver from 'file-saver'
import shortid from 'shortid'

export default {
  dependencies: ['themeColor'],
  data () {
    return {
    }
  },
  created () {
    this.asyncStoreData()
  },
  methods: {
    ...mapActions(['asyncStoreData']),
    linePayAmountWithoutProcessingFee (amount) {
      const processingFee = Math.round(amount * (this.store.linePayProcessingFee || 0) / 100)
      const processingFeeTax = Math.round(processingFee * 0.05)
      return amount - processingFee - processingFeeTax
    },
    format ([from, to]) {
      return this[`format${this.searchUnit.charAt(0).toUpperCase()}${this.searchUnit.slice(1)}`]([from, to])
    },
    formatBusinessHours ([from, to]) {
      return from === 'others'
        ? moment(to).format('MM/DD') + ' 其他'
        : moment(from).format('MM/DD ') + moment(from).format('HH:mm') + ' ~ ' + moment(to).format('HH:mm')
    },
    formatDay ([from]) {
      return moment(from).format('YYYY-MM-DD (dd)')
    },
    formatWeek ([from]) {
      return moment(from).format('YYYY-MM-DD')
    },
    formatMonth ([from]) {
      return moment(from).format('YYYY-MM')
    },
    formatYear ([from]) {
      return moment(from).format('YYYY')
    },
    outputData () {
      return this.sortedCustomStatistics.map(data => {
        return [
          this.format(data.interval),
          data.total.paid.total || 0,
          data.product.paid.total || 0,
          data.order.paid.total || 0,
          parseFloat((data.total.paid.total / data.product.paid.total || 0).toFixed(0)),
          data.total.paid.easyCard || 0,
          parseFloat((data.total.paid.easyCard * (100 - (this.store.easyCardProcessingFee || 0)) / 100 || 0).toFixed(0)),
          (data.total.paid.creditCard || 0) + (data.total.paid.chinaUnionPay || 0),
          parseFloat((((data.total.paid.creditCard || 0) + (data.total.paid.chinaUnionPay || 0)) * (100 - (this.store.creditCardProcessingFee || 0)) / 100 || 0).toFixed(0)),
          data.total.paid.linePayOnline || 0,
          this.linePayAmountWithoutProcessingFee(data.total.paid.linePayOnline || 0),
          data.total.paid.cash || 0,
          this.customForHereStatistics[data.key] ? this.customForHereStatistics[data.key].order.paid.total || 0 : 0,
          parseFloat(((this.customForHereStatistics[data.key] ? this.customForHereStatistics[data.key].order.paid.total || 0 : 0) / this.tableNumbers.length).toFixed(1)),
          data.total.refunded.total || 0,
          data.total.refunded.easyCard || 0,
          (data.total.refunded.creditCard || 0) + (data.total.refunded.chinaUnionPay || 0),
          data.total.refunded.linePayOnline || 0,
          data.total.refunded.cash || 0
        ]
      })
    },
    outputExcel () {
      let titles = []
      let searchUnit = this.$t(`unit.${this.searchUnit}`)
      titles = titles.concat([`時間(${searchUnit})`, '營業額', '來客數', '訂單數', '平均客單價', '悠遊卡', `悠遊卡 (扣除手續費 ${this.store.easyCardProcessingFee || 0}%)`, '信用卡', `信用卡 (扣除手續費 ${this.store.creditCardProcessingFee || 0}%)`, '線上 LINE Pay', `線上 LINE Pay (扣除手續費 ${this.store.linePayProcessingFee || 0}%)`, '現金', '內用訂單數', '翻桌率', '退款總金額', '悠遊卡 (退款)', '信用卡 (退款)', '線上 LNE Pay (退款)', '現金 (退款)'])
      let result = []
      this.outputData().forEach(data => {
        let row = {}
        data.forEach((element, index) => {
          row[titles[index]] = element
        })
        result.push(row)
      })
      const ws = XLSX.utils.json_to_sheet(result)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'People')
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })
      FileSaver.saveAs(new Blob([this.s2ab(wbout)], { type: 'application/octet-stream' }), `${shortid.generate()}.xlsx`)
    },
    s2ab (s) {
      const buf = new ArrayBuffer(s.length)
      const view = new Uint8Array(buf)
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF
      return buf
    }
  },
  computed: {
    ...mapState('statisticsModule', ['customStatistics', 'customForHereStatistics', 'startDate', 'endDate', 'searchUnit']),
    ...mapState(['store', 'tableNumbers']),
    sortedCustomStatistics () {
      return Object.keys(this.customStatistics).sort((a, b) => {
        const momentA = a.endsWith('others') ? moment(a, 'YYYY-MM-DD others').endOf('day') : moment(a)
        const momentB = b.endsWith('others') ? moment(b, 'YYYY-MM-DD others').endOf('day') : moment(b)
        return momentA.isAfter(momentB) ? 1 : -1
      }).map(k => this.customStatistics[k])
    }
  }
}
</script>

<style lang="scss" scoped>
th, td {
  white-space: nowrap;
}

#time-label.label-cell, #time-interval.label-cell {
  padding: 0px 0px 0px 16px;
}
</style>
